/**
 * Global Axios interceptor ສຳລັບ customer website
 * ====================================================
 * ★ ຈັດການ 401 (token ໝົດອາຍຸ) ອັດຕະໂນມັດ
 * ★ Silent Token Refresh — ໃຊ້ refreshToken ຂໍ accessToken ໃໝ່
 * ★ ຈັດການ network error (server ບໍ່ຕອບ)
 * ★ ປ້ອງກັນ redirect ຊ້ຳ (ໃຊ້ flag)
 * ★ Queue pattern — ກັນ refresh ຊ້ຳ ເມື່ອ 401 ມາຫຼາຍ calls ພ້ອมກັນ
 */
import axios from "axios";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";
import { SERVER_URL } from "../api";

const _RKEY = process.env.REACT_APP_ENCRYPTION_KEY;

function _decryptPayload(raw) {
  try {
    if (!raw || typeof raw !== "object" || typeof raw.d !== "string" || !_RKEY) return raw;
    const bytes = CryptoJS.AES.decrypt(raw.d, _RKEY);
    const text = bytes.toString(CryptoJS.enc.Utf8);
    return text ? JSON.parse(text) : raw;
  } catch {
    return raw;
  }
}

let isRedirecting = false;
let isRefreshing = false;
let refreshQueue = []; // ★ Queue: ເກັບ requests ທີ່ລໍຖ້າ refresh ແລ້ວ

// ★ ປ່ອຍ queue ທັງໝົດ — retry ຫຼື reject
function processQueue(error, newToken) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(newToken);
    }
  });
  refreshQueue = [];
}

// ★ Redirect ໄປ login — ລ້າງ token ທັງໝົດ
function forceLogout() {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.removeItem("USER_WEB");
  localStorage.removeItem("ACCESS_TOKEN_WEB");
  localStorage.removeItem("REFRESH_TOKEN_WEB");
  Swal.fire({
    icon: "warning",
    title: "Session ໝົດອາຍຸ",
    text: "ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່",
    confirmButtonText: "ເຂົ້າສູ່ລະບົບ",
    confirmButtonColor: "#f5a623",
    allowOutsideClick: false,
  }).then(() => {
    isRedirecting = false;
    window.location.href = "/login";
  });
}

export function setupAxiosInterceptors() {
  axios.interceptors.response.use(
    // ✅ Success — decrypt ຖ້າ response ຖືກ encrypt ໄວ້
    (response) => {
      if (response?.data?.d) response.data = _decryptPayload(response.data);
      return response;
    },

    // ❌ Error — ຈັດການ 401 + network errors
    async (error) => {
      const originalRequest = error.config;
      const status = error?.response?.status;
      const errCode = error?.response?.data?.message || error?.response?.data?.error;

      // ===== 401 Unauthorized: Token ໝົດອາຍຸ ຫຼື ບໍ່ຖືກຕ້ອງ =====
      if (status === 401 && !originalRequest._retry) {
        // ບໍ່ retry ຖ້າເປັນ auth endpoints ຫຼື refresh-token endpoint ເອງ
        const url = originalRequest?.url || "";
        const isAuthEndpoint = url.includes("/login") || url.includes("/register") || url.includes("/forget-password");
        const isRefreshEndpoint = url.includes("/refresh-token");
        
        if (isAuthEndpoint || isRefreshEndpoint) {
          return Promise.reject(error);
        }

        // ★ Token expired — ລອງ refresh ອັດຕະໂນມັດ
        const tokenErrors = ["TOKEN_EXPIRED", "INVALID_TOKEN", "AUTHORIZATION_REQUIRED"];
        if (tokenErrors.includes(errCode) || !errCode) {
          const refreshToken = (() => {
            try {
              const raw = localStorage.getItem("REFRESH_TOKEN_WEB");
              if (raw && raw !== "undefined" && raw !== "null") return JSON.parse(raw);
            } catch (e) { /* ignore */ }
            return null;
          })();

          // ★ ບໍ່ມີ refreshToken → logout ທັນທີ (ພຶດຕິກຳເກົ່າ)
          if (!refreshToken) {
            forceLogout();
            return Promise.reject(error);
          }

          // ★ ຖ້າກຳລັງ refresh ຢູ່ → ເຂົ້າ queue ລໍຖ້າ
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              refreshQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              originalRequest._retry = true;
              return axios(originalRequest);
            }).catch(() => {
              return Promise.reject(error);
            });
          }

          // ★ ເລີ່ມ refresh
          isRefreshing = true;
          originalRequest._retry = true;

          try {
            const res = await axios.post(
              `${SERVER_URL}/api/v1/refresh-token`,
              { refreshToken }
            );
            const newAccessToken = res.data.accessToken;

            // ★ ເກັບ token ໃໝ່
            localStorage.setItem("ACCESS_TOKEN_WEB", JSON.stringify(newAccessToken));

            // ★ ປ່ອຍ queue — ທຸກ request ທີ່ລໍຖ້າ ຈະ retry ດ້ວຍ token ໃໝ່
            processQueue(null, newAccessToken);

            // ★ Retry request ເດີມ
            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // ★ Refresh ລົ້ມ → ລ້າງທັງໝົດ → redirect login (ພຶດຕິກຳເກົ່າ)
            processQueue(refreshError, null);
            forceLogout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      // ===== Network Error: Server ບໍ່ຕອບ =====
      if (!error.response && error.message === "Network Error") {
        console.warn("Network Error - server unreachable");
        // ບໍ່ show popup ທຸກຄັ້ງ — ໃຫ້ component handle ເອງ
      }

      return Promise.reject(error);
    }
  );
}
